var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import format from 'string-format';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import ToggleButton from '@mui/material/ToggleButton';
import Tooltip from '@mui/material/Tooltip';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTheme } from '@mui/material/styles';
import { OGPseudoTable, OGPseudoTableBody, OGPseudoTableCell, OGPseudoTableRow } from '@opengear/table';
import { useOpen } from '@/hooks/open';
import { createBarStyles, ExpandRowTableCell, NameCell, PermissionCell } from '@/components/accounts/utils';
function isRolePermission(permissions) {
    var firstValue = Object.values(permissions)[0];
    return firstValue === 'read_write' || firstValue === 'read_only' || firstValue === 'deny';
}
export function PermissionsSectionRow(_a) {
    var children = _a.children, _b = _a.unmountOnExit, unmountOnExit = _b === void 0 ? true : _b, permissions = _a.permissions, _c = _a.roleName, roleName = _c === void 0 ? 'overall' : _c, title = _a.title, onClick = _a.onClick;
    var theme = useTheme();
    var t = useTranslation().t;
    var _d = useOpen(), open = _d.open, handleToggle = _d.handleToggle;
    var uuid = useId();
    var enableAccessActions = !!onClick && isRolePermission(permissions);
    var readWriteTooltipLabel = format(t('components.permissions.table.access_actions.read_write'), title);
    var readOnlyTooltipLabel = format(t('components.permissions.table.access_actions.read_only'), title);
    var denyTooltipLabel = format(t('components.permissions.table.access_actions.deny'), title);
    var counts = Object.entries(permissions).reduce(function (accumulator, currentValue) {
        var _a = __read(currentValue, 2), value = _a[1];
        var valueCheck = typeof value === 'object' ? value[roleName] : value;
        switch (valueCheck) {
            case 'read_write':
                accumulator.read_write += 1;
                accumulator.total += 1;
                break;
            case 'read_only':
                accumulator.read_only += 1;
                accumulator.total += 1;
                break;
            case 'deny':
                accumulator.deny += 1;
                accumulator.total += 1;
                break;
        }
        return accumulator;
    }, {
        deny: 0,
        read_only: 0,
        read_write: 0,
        total: 0,
    });
    function normalize(value, total) {
        return (value * 100) / total;
    }
    var handleReadWriteAccessAction = useCallback(function () {
        if (enableAccessActions) {
            onClick('read_write', permissions);
        }
    }, [enableAccessActions, onClick, permissions]);
    var handleReadOnlyAccessAction = useCallback(function () {
        if (enableAccessActions) {
            onClick('read_only', permissions);
        }
    }, [enableAccessActions, onClick, permissions]);
    var handleDenyAccessAction = useCallback(function () {
        if (enableAccessActions) {
            onClick('deny', permissions);
        }
    }, [enableAccessActions, onClick, permissions]);
    return (_jsxs(_Fragment, { children: [_jsxs(OGPseudoTableRow, __assign({ "aria-label": title }, { children: [_jsx(ExpandRowTableCell, { children: _jsx(ToggleButton, __assign({ "aria-controls": "permissions-section-content-".concat(uuid), "aria-expanded": open, "aria-labelledby": "permissions-section-title-".concat(uuid), onChange: handleToggle, sx: { padding: 0 }, selected: open, value: "check" }, { children: open ? _jsx(ExpandLessIcon, {}) : _jsx(ExpandMoreIcon, {}) })) }), _jsx(NameCell, __assign({ id: "permissions-section-title-".concat(uuid) }, { children: title })), _jsx(PermissionCell, { children: _jsxs(Box, __assign({ textAlign: "center" }, { children: [_jsx(Tooltip, __assign({ arrow: true, "aria-hidden": !enableAccessActions, enterDelay: 500, placement: "top", title: readWriteTooltipLabel }, { children: _jsx("span", { children: _jsx(IconButton, __assign({ "aria-label": readWriteTooltipLabel, disabled: !enableAccessActions, size: "small", onClick: handleReadWriteAccessAction }, { children: _jsx(CheckCircleOutlineIcon, { color: "success", sx: { marginX: 'auto' } }) })) }) })), _jsx(LinearProgress, { "aria-label": t('components.permissions.table.headers.read_write'), "aria-valuemin": 0, "aria-valuemax": counts.total, "aria-valuenow": counts.read_write, color: "success", role: "meter", value: normalize(counts.read_write, counts.total), variant: "determinate", sx: createBarStyles(theme) })] })) }), _jsx(PermissionCell, { children: _jsxs(Box, __assign({ textAlign: "center" }, { children: [_jsx(Tooltip, __assign({ arrow: true, "aria-hidden": !enableAccessActions, enterDelay: 500, placement: "top", title: readOnlyTooltipLabel }, { children: _jsx("span", { children: _jsx(IconButton, __assign({ "aria-label": readOnlyTooltipLabel, disabled: !enableAccessActions, size: "small", onClick: handleReadOnlyAccessAction }, { children: _jsx(VisibilityIcon, { color: "warning", sx: { marginX: 'auto' } }) })) }) })), _jsx(LinearProgress, { "aria-label": t('components.permissions.table.headers.read_only'), "aria-valuemin": 0, "aria-valuemax": counts.total, "aria-valuenow": counts.read_only, color: "warning", role: "meter", value: normalize(counts.read_only, counts.total), variant: "determinate", sx: createBarStyles(theme) })] })) }), _jsx(PermissionCell, { children: _jsxs(Box, __assign({ textAlign: "center" }, { children: [_jsx(Tooltip, __assign({ arrow: true, "aria-hidden": !enableAccessActions, enterDelay: 500, placement: "top", title: denyTooltipLabel }, { children: _jsx("span", { children: _jsx(IconButton, __assign({ "aria-label": denyTooltipLabel, disabled: !enableAccessActions, size: "small", onClick: handleDenyAccessAction }, { children: _jsx(DoDisturbIcon, { color: "error", sx: { marginX: 'auto' } }) })) }) })), _jsx(LinearProgress, { "aria-label": t('components.permissions.table.headers.deny'), "aria-valuemin": 0, "aria-valuemax": counts.total, "aria-valuenow": counts.deny, color: "error", role: "meter", value: normalize(counts.deny, counts.total), variant: "determinate", sx: createBarStyles(theme) })] })) })] })), _jsx(OGPseudoTableRow, { children: _jsx(OGPseudoTableCell, __assign({ sx: { border: 'none', padding: 0, width: '100%' } }, { children: _jsx(Collapse, __assign({ in: open, sx: { width: '100%' }, unmountOnExit: unmountOnExit }, { children: _jsx(OGPseudoTable, __assign({ id: "permissions-section-content-".concat(uuid) }, { children: _jsx(OGPseudoTableBody, { children: children }) })) })) })) })] }));
}
